import React from 'react';
import { Plane, Moon, Clock, Star, Map, Car, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useIsMobile from '@/hooks/useIsMobile';

const ICON_MAP = { airport: Plane, night: Moon, disposal: Clock, event: Star, excursion: Map, city: Car, protection: ShieldCheck };

const Services = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const services = t('services_list', { returnObjects: true });
  const total = services.length;

  return (
    <section id="services" className="bg-[#0A0A0A] py-20 px-4" data-testid="services-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-[#EAB308] font-semibold mb-3">{t('services.label')}</p>
          <h2 className="text-3xl sm:text-4xl font-light text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('services.title')}
          </h2>
          <p className="text-[#A3A3A3] mt-3 text-base max-w-xl mx-auto">{t('services.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((svc, i) => {
            const Icon = ICON_MAP[svc.key] || Car;
            const isOrphan = i === total - 1 && total % 3 === 1;
            const orphanClass = isOrphan ? 'sm:col-span-2 lg:col-span-1 lg:col-start-2' : '';
            return (
              <motion.div
                key={svc.key}
                {...(!isMobile && { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5, delay: i * 0.08 } })}
                data-testid={`service-card-${svc.key}`}
                className={`bg-[#1A1A1A] border border-white/8 rounded-2xl p-6 hover:border-[#EAB308]/30 transition-colors duration-300 group ${orphanClass}`}
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#EAB308]/10 mb-4 group-hover:bg-[#EAB308]/20 transition-colors duration-300">
                  <Icon size={18} className="text-[#EAB308]" />
                </div>
                <h3 className="font-semibold text-white text-base mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{svc.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{svc.desc}</p>
                {svc.key === 'protection' && (
                  <a
                    href="https://wa.me/33674626368"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="protection-cta-btn"
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-xl bg-[#EAB308]/10 border border-[#EAB308]/30 text-[#EAB308] text-xs font-semibold hover:bg-[#EAB308]/20 hover:border-[#EAB308]/60 transition-all duration-200"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {t('booking.protection_cta')}
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
